import { Link } from 'react-router'
import { formatPostDate, getListablePosts } from '#app/utils/blog.ts'
import { buildMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/_index.ts'

export function loader() {
	// Only frontmatter crosses the network - the MDX component itself is
	// resolved from the bundled registry on whichever side is rendering.
	//
	// The date is formatted here rather than in the component on purpose. The
	// component must not import from `blog.ts`: that module eagerly globs every
	// MDX post, so a single reference from client-side code pulls the compiled
	// text of the whole blog into this route's bundle. Keeping the import
	// loader-only lets React Router strip it from the client build entirely.
	return {
		posts: getListablePosts().map((post) => ({
			...post.meta,
			formattedDate: formatPostDate(post.meta.publishedAt),
		})),
	}
}

// `private`, not `public`: the root loader embeds session data in the
// document, so a shared cache must never store this response.
export const headers: Route.HeadersFunction = () => ({
	'Cache-Control': `private, max-age=${5 * 60}`,
})

export const links: Route.LinksFunction = () => [
	{
		rel: 'alternate',
		type: 'application/rss+xml',
		title: 'Syrcha Blog',
		href: '/rss.xml',
	},
]

export const meta: Route.MetaFunction = ({ matches }) =>
	buildMeta({
		title: 'Blog - Privacy, Accessibility & AI Act Compliance | Syrcha',
		description:
			'Practical guidance on GDPR, CCPA, WCAG and the EU AI Act for people who build and run websites. Checklists you can act on, not marketing theatre.',
		matches,
	})

export default function BlogIndexRoute({ loaderData }: Route.ComponentProps) {
	const { posts } = loaderData

	return (
		// The marketing layout owns the single `<main>` landmark.
		<div className="bg-[var(--marketing-base)]">
			<section className="border-b border-[var(--marketing-border)] pt-16 pb-14 sm:pt-24">
				<div className="mx-auto max-w-3xl px-6">
					<p className="font-[family-name:var(--font-mono)] text-[13px] text-[var(--marketing-faint)]">
						/ blog
					</p>
					<h1 className="mt-3 font-[family-name:var(--font-display)] text-[38px] leading-[1.05] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[44px]">
						Compliance, explained properly.
					</h1>
					<p className="mt-5 max-w-[48ch] text-[17px] leading-relaxed text-[var(--marketing-gray)]">
						Practical guidance on GDPR, CCPA, WCAG and the EU AI Act for the
						people who actually have to ship the fix. Every post ends with
						something you can check today.
					</p>
				</div>
			</section>

			<section className="py-14 sm:py-20">
				<div className="mx-auto max-w-3xl px-6">
					{posts.length === 0 ? (
						<p className="text-[15px] text-[var(--marketing-gray)]">
							Nothing published yet; the first posts are on their way.
						</p>
					) : (
						<ul className="flex flex-col gap-10">
							{posts.map((post) => (
								<li
									key={post.slug}
									className="border-b border-[var(--marketing-border)] pb-10 last:border-b-0 last:pb-0"
								>
									<div className="flex flex-wrap items-center gap-3 font-[family-name:var(--font-mono)] text-[12px] text-[var(--marketing-faint)]">
										<time dateTime={post.publishedAt}>
											{post.formattedDate}
										</time>
										{post.readingMinutes ? (
											<span>· {post.readingMinutes} min read</span>
										) : null}
										{post.draft ? (
											<span className="rounded-full border border-[var(--marketing-warn)] px-2 py-0.5 text-[var(--marketing-warn)]">
												draft, not public
											</span>
										) : null}
									</div>

									<h2 className="mt-3 font-[family-name:var(--font-display)] text-[24px] leading-[1.15] font-semibold tracking-[-0.01em]">
										<Link
											to={`/blog/${post.slug}`}
											className="text-[var(--marketing-black)] transition-colors hover:text-[var(--marketing-accent)]"
										>
											{post.title}
										</Link>
									</h2>

									{/* `48ch` ≈ 75 real characters in DM Sans - the same measured
									    cap the legal pages use. Unbounded, these excerpts ran
									    ~110 characters at `max-w-3xl`. */}
									<p className="mt-3 max-w-[48ch] text-[15px] leading-relaxed text-[var(--marketing-gray)]">
										{post.description}
									</p>

									{post.tags.length > 0 ? (
										<ul className="mt-4 flex flex-wrap gap-2">
											{post.tags.map((tag) => (
												<li
													key={tag}
													className="rounded-full border border-[var(--marketing-border)] px-3 py-1 font-[family-name:var(--font-mono)] text-[11px] text-[var(--marketing-faint)]"
												>
													{tag}
												</li>
											))}
										</ul>
									) : null}
								</li>
							))}
						</ul>
					)}
				</div>
			</section>
		</div>
	)
}
